import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { commitTranslationFile, getTranslationFile, saveTranslationFile, uploadTranslationFile } from '../../../../connection/translations';
import ModalComponent from '../../../general-ui/modal/modal';
import LargeButton from '../../../general-ui/button/large-button';
import { hideAlert, showAlert, showAlertLong } from '../../../general-ui/alert/alert-service';
import LanguageSelectInput from '../../../general-ui/select/language-select';
import { PrimaryTooltip } from '../session/session-modal';
import TranslateLangs from '../../../../utils/translate-langs.json';
import { blurEditor, enableTranslationsV3, loadWorkingEvent, lockEventSave, releaseEventSaveLock, setTranslationManifests, setWorkingEvent } from '../../../../store/actions/admin/create-event';
import { BrandliveEvent, FeatureFlagsEnum, Language, TranslationUploadWarning } from '../../../../types/working-model';
import SelectInput from '../../../general-ui/select/select';
import { LazyMotion, domAnimation, m, AnimatePresence } from "framer-motion";
import translateLangsMap from '../../../../utils/translate-langs-map.json';
import { LanguagesAbbr } from '../../../../types/working-model';
import WaitingIndicator from '../../../general-ui/waiting-indicator/waiting-indicator';
import { toArrayMap, userToConnectedUser } from '../../../../utils/utils';
import { useAppDispatch, useThunkDispatch, useTypedSelector } from '../../../../store/reducers/use-typed-selector';
import { streamsLoader } from '../../../../i18n/config';
import Socket from '../../../../connection/socket';
import SocketManager from "../../../../connection/socket-main-thread/socket-manager";
import Switch from "../../../general-ui/switch/switch";
import { toggleTranslationsV2 } from '../../../../store/actions/admin/create-event';
import { getLogger } from '../../../../utils/debug-logger';

const logger = getLogger('bl-admin:translations');

import './event-translations-modal.scss';
import { workingEventDefaultLanguage } from '../../utils/utils';
import { UpdateSelectedEventDefaultLanguage } from '../../../../connection/events';
import { loadWorkingSession } from '../../../../store/actions/admin/create-event/session';
import { GetSingleSession } from 'connection/sessions';

interface EventTranslationsModalProps {
	open: boolean;
	closeFn: () => void;
	languages: string[];
	workingEvent: BrandliveEvent;
}

const getLangWarning = (uploadWarnings: TranslationUploadWarning[]) => {
	const langWarnings = uploadWarnings.filter(warning => warning.type === 'Incorrect Language');
	const langGroups = toArrayMap('value', langWarnings);
	return Array.from(langGroups.entries()).sort((a, b) => a[1] < b[1] ? 1 : -1)[0] as [string, TranslationUploadWarning[]];
};

const rmHtml = (str: string) => {
	const div = document.createElement('div');
	div.innerHTML = str;
	return div.innerText;
};

const EventTranslationsModal = ({ open, closeFn, languages, workingEvent }: EventTranslationsModalProps): JSX.Element => {
	const savingEvent = useTypedSelector(state => state.CreateEventReducer.saving);
	const token = useTypedSelector(state => state.AuthReducer.token);
	const user = useTypedSelector(state => state.AuthReducer.user);
	const translationVersion = useTypedSelector((state) => Number(state.CreateEventReducer.workingEvent?.settings?.translation_version));
	const v3flag = useTypedSelector(state => state.FeatureFlagsReducer.featureFlags[FeatureFlagsEnum.translations_v3]);
	const workingSession = useTypedSelector(state => state.CreateSessionReducer.workingSession);

	// using V1 is not flag dependent
	const usingV1 = (workingEvent?.settings.translation_version === undefined || Number(workingEvent?.settings.translation_version) === 1);

	// using V2 if translation_version is set to 2 OR if translation_version is set to 3 and the v3flag is off
	const usingV2 = Number(workingEvent?.settings.translation_version) === 2 || (Number(workingEvent?.settings.translation_version) === 3 && !v3flag);

	// using V3 if translation version is set to 3 AND the v3flag is on
	const usingV3 = Number(workingEvent?.settings.translation_version === 3) && !!v3flag;

	const dispatch = useAppDispatch();
	const defaultLanguage = workingEventDefaultLanguage(workingEvent);
	const defaultEventLanguage = useMemo(() => {
		return TranslateLangs.find(lang => lang.code === defaultLanguage) as Language;
	}, [defaultLanguage]);

	const sessionLanguages = useMemo(() => {
		if (!workingEvent) return [];

		const uniqLangs = new Set(
			workingEvent.sessions.flatMap(session => session.languages)
		);

		return TranslateLangs.filter(lang => uniqLangs.has(lang.code));
	}, [workingEvent]);

	const [uploadWarnings, setUploadWarnings] = useState<TranslationUploadWarning[]>([]);
	const [uploadComplete, setUploadComplete] = useState(false);
	const [processingUpload, setProcessingUpload] = useState(false);
	const [selectedLanguage, setSelectedLanguage] = useState('');
	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const [savingEventLanguage, setSavingEventLanguage] = useState(false);
	const [saving, setSaving] = useState(false);
	const [downloading, setDownloading] = useState(false);
	const [v3UpgradeTriggered, setV3UpgradeTriggered] = useState(false);

	const socket = useRef<Socket | null>(null);

	const activeChannel = useMemo(() => user?.active_channel, [user]);

	useEffect(() => {
		logger('event translation state', { usingV1, usingV2, usingV3 });
	}, [usingV1, usingV2, usingV3]);

	useEffect(() => {
		if (activeChannel && user && workingEvent) {
			socket.current = SocketManager.get(`admin-${activeChannel}`, {
				admin: userToConnectedUser(user, workingEvent.uuid)
			});
		}
	}, [dispatch, activeChannel, user, workingEvent]);

	const downloadFn = async () => {
		if (!token || !workingEvent) return;

		setDownloading(true);
		getTranslationFile(token, workingEvent.uuid)
			.then(resp => {
				if (resp.status === 200) {
					return resp.blob();
				}
				throw ('Unable to fetch report');
			})
			.then(blob => {
				const url = window.URL.createObjectURL(blob);
				const a = document.createElement('a');
				a.style.display = 'none';
				a.href = url;
				a.download = `${workingEvent?.name}_Translations.zip`;
				document.body.appendChild(a);
				a.click();
				window.URL.revokeObjectURL(url);

				// if we're on V3 and this event previously used V1, force event to use V3
				if (v3flag && usingV1) {
					logger('V3 flag is on and event is on V1, force-transitioning event to use V3');
					dispatch(enableTranslationsV3());
				}

				logger('Downloaded translations excel for event');
			})
			.catch((e) => {
				console.error(e);
				showAlert({ message: 'Download failed', description: 'Your translations failed to download.', duration: 3000, type: 'error' });
				console.error('Translations download failed for event: ', workingEvent.uuid);
			}).finally(() => {
				setDownloading(false);
			});
	};

	const handleFile = async (file: File | FileList) => {
		if (file instanceof File && token && workingEvent) {
			setSelectedFile(file);
		}
	};

	const commitUpload = async () => {
		if (selectedFile && selectedLanguage && token && workingEvent) {
			try {
				logger('Saving uploaded translations');
				if (user && socket.current) {
					socket.current.sendMessage({
						type: 'translations-updating',
						data: {
							isUpdating: true,
							admin: {
								profile_id: user.id,
								first_name: user.profile.first_name,
								last_name: user.profile.last_name
							},
							eventUuid: workingEvent.uuid
						}
					});
				}
				setUploadWarnings([]);
				setUploadComplete(false);
				setProcessingUpload(true);
				const res = await uploadTranslationFile(token, selectedFile, workingEvent.uuid, selectedLanguage);
				dispatch(setTranslationManifests(res.manifests));
				setUploadWarnings(res.warnings);
				setUploadComplete(true);
			} catch (e: unknown) {
				showAlertLong({
					message: 'Unable to process upload',
					description: (e as { error: { message: string } }).error.message,
					type: 'error'
				});
			} finally {
				setProcessingUpload(false);
				if (socket.current) {
					socket.current.sendMessage({
						type: 'translations-updating',
						data: {
							isUpdating: false,
							admin: null,
							eventUuid: workingEvent.uuid
						}
					});
				}
			}
		}
	};

	/**
	 * Update the working event's base language.
	 */
	const handleLanguageChange = useCallback(async (_: Language[], defaultLang: Language) => {
		let changingLanguageAlert;
		try {
			if (token && defaultLang && workingEvent && defaultLang.code !== workingEvent?.settings?.i18n?.base) {
				setSavingEventLanguage(true);

				changingLanguageAlert = showAlertLong({
					message: 'Updating event language',
					description: 'Please wait while we update your event content. This may take a few moments.',
					spinner: true
				}) as string;

				const event = await UpdateSelectedEventDefaultLanguage({ token, eventUuid: workingEvent.uuid, language: defaultLang.code });
				dispatch(setWorkingEvent(event));

				if (workingSession) {
					const replacementSession = event.sessions.find(session => session.uuid === workingSession.uuid);
					if (replacementSession) {
						dispatch(loadWorkingSession(replacementSession));
					}
				}
			}
		} catch (e) {
			console.error(e);
			showAlert({
				message: 'Error updating language',
				description: 'We were unable to change your event default language. Please ensure that your event has content in the selected language.',
				type: 'error'
			});
		} finally {
			if (changingLanguageAlert) {
				hideAlert({ id: changingLanguageAlert });
			}

			setSavingEventLanguage(false);
		}

	}, [dispatch, token, workingEvent, workingSession]);

	const close = useCallback(() => {
		setSelectedLanguage('');
		setSelectedFile(null);
		closeFn();
	}, [closeFn]);

	const uploadWarningMessage = useMemo(() => {
		const missingKeyWarnings: JSX.Element[] = [];
		const missingCellWarnings: JSX.Element[] = [];

		for (const [key, value] of uploadWarnings.entries()) {
			if (value.type === "Missing Key") {
				missingKeyWarnings.push(
					<div key={`key-warning-${key}`}>
						<i>{value.value}</i>
					</div>
				);
			}
			if (value.type === "Missing Cell") {
				missingCellWarnings.push(
					<div key={`cell-warning-${key}`}>
						<i>{value.value}</i>
					</div>
				);
			}
		}
		return (
			(missingKeyWarnings.length || missingCellWarnings.length) ? (
				<div className='upload-warnings-container'>
					{missingKeyWarnings.length > 0 ? (
						<div className="missing-key-warnings">
							<h6>The following rows were missing from the upload</h6>
							<div className="missing-keys">
								{missingKeyWarnings}
							</div>
						</div>
					) : null}
					{missingCellWarnings.length > 0 ? (
						<div className="missing-cell-warnings">
							<h6>The following cells were missing from the upload</h6>
							<div className="missing-keys">
								{missingCellWarnings}
							</div>
						</div>
					) : null}
				</div>
			) : null
		);
	}, [uploadWarnings]);

	const changeLanguage = (value: string) => {
		setSelectedLanguage(value);
	};

	const languageOptions = useMemo(() => {
		return [{ label: "Select Language", value: "" }, ...languages.map(
			lang => (
				{
					label: `${translateLangsMap[lang as unknown as LanguagesAbbr]} - ${lang}`,
					value: lang
				}
			)
		)];
	}, [languages]);

	const closeUploadCompleteModal = useCallback(() => {
		setUploadWarnings([]);
		setUploadComplete(false);
		close();
		if (usingV3 && workingEvent && token) {
			logger('Using V3, so must refresh editor');
			// for V3, we need to ensure that the content automatically updates
			showAlert({
				message: "Fetching updated translations",
				description: "Please wait a moment while we refresh the editor.",
				duration: 5000,
			});
			dispatch(blurEditor(true));
			dispatch(loadWorkingEvent(workingEvent.uuid, token));
		}
	}, [workingEvent, token, close, usingV3, dispatch]);

	const applyTranslations = useCallback(async () => {
		if (!token || !workingEvent) {
			return;
		}

		try {
			setSaving(true);

			// block save requests from being sent to the server while this is processing
			dispatch(lockEventSave());

			const saved = await saveTranslationFile(token, workingEvent.uuid);
			if (!saved) {
				throw new Error(`Unable to save translation file for ${workingEvent.uuid}`);
			}

			if (v3flag) {
				// returns the updated event bundle
				const updatedEvent = await commitTranslationFile(token, workingEvent.uuid);

				if (!updatedEvent) {
					throw new Error(`Unable to commit translation file for ${workingEvent.uuid}`);
				}

				// force-update redux
				dispatch(setWorkingEvent(updatedEvent));

				// update workingSession
				if (workingSession) {
					const replacementWorkingSession = await GetSingleSession(token, workingSession.session);
					if (replacementWorkingSession) {
						dispatch(loadWorkingSession(replacementWorkingSession));
					}
				}

				// tell translation loader service to only use static remotes
				// and stop loading all remotes
				streamsLoader.useStaticRemote = true;
				streamsLoader.useAllRemote = false;

				// V3 feature flag is enabled, but event is not on v3 yet.  Upgrade.
				if (!usingV3) {
					dispatch(enableTranslationsV3());
					setV3UpgradeTriggered(true);
				}
			} else {
				// in the case that this event has never had a V2 translation uploaded before, tell the translation loader
				// to load all remotes
				streamsLoader.useAllRemote = true;
			}

			closeUploadCompleteModal();
			streamsLoader.refresh();
			close();
		} catch (e) {
			console.error(e);
			showAlertLong({
				message: 'Unable to apply translations',
				description: 'We ran into an issue applying your translations. Please try again later.',
				type: 'error'
			});
		} finally {
			setSaving(false);

			// release save lock 
			dispatch(releaseEventSaveLock());
		}
	}, [
		token,
		workingEvent,
		dispatch,
		v3flag,
		usingV3,
		closeUploadCompleteModal,
		close,
		workingSession,
	]);


	// When a V2 event has translations applied, it will be upgraded to V3.
	// This process happens on the backend so that all the processing happens in the
	// correct way.  However, when the front end refreshes, it still acts as V2.
	// If an upgrade event is detected, wait until confirmed V3 and then perform another refresh.
	useEffect(() => {
		// Triggered v3 upgrade AND upgrade complete
		if (v3UpgradeTriggered && usingV3) {
			logger('version upgrade triggered, refreshing streams loader');
			streamsLoader.refresh();
		}
	}, [usingV3, v3UpgradeTriggered]);

	const handleToggleForTranslation = () => {
		if (!v3flag) {
			dispatch(toggleTranslationsV2(translationVersion === 1));
		} else {
			logger('V3 flag is enabled but somehow we attempted to toggle event to V2. Ignoring.');
		}
	};

	useEffect(() => {
		if (!workingEvent || !token) {
			return;
		}

		// the translation version is 2+ and there has never been a default event language set on this event
		// so set one. 
		if (translationVersion >= 2 && !workingEvent.settings.i18n?.base) {
			handleLanguageChange([], defaultEventLanguage);
		}
	}, [workingEvent, token, translationVersion, handleLanguageChange, defaultEventLanguage]);

	const incorrectLanguageWarning = useMemo(() => {
		const incorrectLanguage = getLangWarning(uploadWarnings);

		if (incorrectLanguage?.[0]) {
			const meta = incorrectLanguage[1]?.[0].metadata as { sheet: string, key: string, value: string };
			return (
				<>
					<div className="missing-language-warning">
						<h4>Incorrect Language Detected</h4>
						<p><strong className="error">{incorrectLanguage[0]}</strong></p>
						<h6>Example:</h6>
						<blockquote>
							<section>
								<label>Sheet</label>
								<p><strong>{meta?.sheet}</strong></p>
							</section>

							<section>
								<label>Key</label>
								<p>{meta?.key}</p>
							</section>

							<section>
								<label>Entry</label>
								<p><em>{rmHtml(meta?.value)}</em></p>
							</section>
						</blockquote>
					</div>
				</>
			);
		}
	}, [uploadWarnings]);


	// https://brandlive.atlassian.net/browse/BP-2448
	// Download modal should always display after moving to V3.
	// If v3 is FF off, other things will check to make sure it pulls the correct translation.
	// Display here will always display the download option, as it doesn't affect the translation version.
	return (
		<ModalComponent
			title="Event Translations"
			open={open}
			padTitle={false}
			onRequestClose={close}
			className={"translations-modal"}
			closeable
			cancellable
			header={!v3flag ? (
				<div className="row translations-toggle">
					<span className="switch-label"> Enable .xls Translations</span>
					<Switch
						value={'use-translation-v2'}
						on={usingV2}
						onClick={handleToggleForTranslation}
					/>
				</div>
			) : undefined}
			footer={(!usingV1 || v3flag) ?
				// should show modal footer if using V2 or V3 is flagged on
				(
					<div className="translations-footer">
						<button
							disabled={downloading}
							style={{ marginRight: 'auto' }}
							onClick={downloadFn}>
							{!downloading ? (
								<>
									Download Translations
								</>
							) : (
								<>
									<span style={{ marginRight: 6 }}>Generating file</span>
									<WaitingIndicator />
								</>
							)}
						</button>

						<div className="translations-footer-actions">
							<button style={{ marginLeft: 'auto' }} onClick={close}>Close</button>
							<button className="lemonade" disabled={!(selectedLanguage && selectedFile)} style={{ marginLeft: 'auto' }} onClick={commitUpload}>Upload</button>
						</div>
					</div>
				) :
				// should hide modal footer if using V1 and V3 is not flagged on
				undefined
			}
		>
			{/* body modal */}
			<div className="translations-modal">
				{(usingV1 && !v3flag) ?
					// when V3 is not flagged on and user has not enabled V2, show the "enable .xls translations" message
					(
						<div>
							<p>Toggle on to enable the .xls translation flow. This will enable you to add additional languages across your event and export all site content to a .xls for offline translation / localization and re-upload to the platform.</p>
							<p>After upload, translated content will be <strong>read-only</strong> and can be updated using the download / upload process.</p>
							<p>Click <a href="https://support.brandlive.com/hc/en-us/articles/31426909320347-How-to-Add-Languages-Edit-Translations" target="_blank" rel="noreferrer">here</a> to read the full documentation on .xls translation workflow.</p>
						</div>
					) : (
						<div>
							<LanguageSelectInput
								label={"Default Event Language"}
								tooltip={[PrimaryTooltip, "Your default language is used across the event to generate translations and as a fallback for text untranslated in a given language. Changing your default language will prevent auto-translation in the original language."]}
								placeholder={"Select language"}
								onChange={handleLanguageChange}
								languages={[defaultEventLanguage]}
								defaultLanguage={defaultEventLanguage}
								limit={1}
								chooseableLanguages={sessionLanguages}
								disableDefault
								disableInput={savingEventLanguage || savingEvent}
								disableDefaultRemoval
							/>
							<LargeButton
								style={{ marginTop: 0 }}
								onFile={handleFile}
								title="Select translation document"
								allowedFileTypes={['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet']}
								uploading={processingUpload}
							/>
							<AnimatePresence>
								{!!selectedFile && (
									<LazyMotion features={domAnimation}>
										<m.div
											initial={{ maxHeight: 0, opacity: 0 }}
											animate={{ maxHeight: 200, opacity: 1 }}
											exit={{ maxHeight: 0, opacity: 0 }}
											className="translations-language-select">
											<SelectInput
												label={"Select Language"}
												options={languageOptions}
												selected={selectedLanguage}
												onChange={changeLanguage}
												placeholder={"Select Excel file language"}
												ignoreInlineStyling={true}
											/>
										</m.div>
									</LazyMotion>
								)}
							</AnimatePresence>

							<div>
								<ModalComponent
									open={uploadComplete}
									closeable
									cancellable
									onRequestClose={() => setUploadComplete(false)}
									title={'Your upload has finished processing'}
									className={"post-processing-modal"}
									footer={
										<>
											<button onClick={() => {
												setUploadWarnings([]);
												setUploadComplete(false);
											}
											}>Reupload</button>
											<button onClick={() => {
												setUploadWarnings([]);
												setUploadComplete(false);
												close();
											}
											}>Close</button>
											<button
												className="lemonade"
												disabled={saving}
												style={{ marginLeft: 'auto' }}
												onClick={applyTranslations}
											>
												{saving ? (
													<WaitingIndicator />
												) : (
													"Apply Translations"
												)}
											</button>
										</>
									}
								>
									<div>
										{
											uploadComplete && !uploadWarningMessage && (
												<div className="upload-complete-message">
													<h5>Upload complete</h5>
												</div>
											)
										}
										{incorrectLanguageWarning}
										{uploadWarningMessage && (
											<div className="warning-messages">
												<h4>The following fields were not translated</h4>
												{uploadWarningMessage}
											</div>
										)}
									</div>
								</ModalComponent>
							</div>
						</div>
					)}
			</div>
		</ModalComponent>
	);
};

export default EventTranslationsModal;